import { IGE } from '@hub/free-form-hub/config';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { useState } from 'react';
import type { NbsResponse } from '@hub/free-form-hub/common/uploadFile/fetchNbsRecommendation';

export interface IgeResponseData {
  unitPrice: {
    value: number;
    currency: string;
    dt: number;
    dtText: string;
  };
  widenImageId: string;
  shopUrl: string;
  articleNumber: string;
  articlesCount: number;
}

export interface IgeRecommendationResults {
  category?: string;
  nameKey?: string;
  technicalDrawingWidenId?: string;
  data?: IgeResponseData;
}

export interface IgeRecommendationRequestPayload {
  dimensions?: {
    b2?: number;
    d1?: number;
    d5?: number;
    hubDesign?: string;
    module?: number;
    numberOfTeeth?: number;
  };
}

export interface DefaultGearData {
  nameKey: string;
  shopLink: string;
  widen: string;
  technicalDrawingWidenId?: string;
}

const defaultGearData: Record<string, DefaultGearData> = {
  gears_spur_straight: {
    nameKey: 'SPUR_GEAR',
    shopLink: '/gears/plastic-gears',
    widen: 'wdnepkcjjn',
    technicalDrawingWidenId: 'quhz8nzsom',
  },
  gears_beveled_straight: {
    nameKey: 'BEVEL_GEAR',
    shopLink: '/gears/plastic-bevel-gears',
    widen: 'irgsylaj9i',
  },
  gear_racks_straight: {
    nameKey: 'GEAR_RACK',
    shopLink: '/gears/plastic-gear-racks',
    widen: 'vvsiozkkvc',
  },
};

export const useIgeRecommendation = () => {
  const [igeRecommendation, setIgeRecommendations] = useState<IgeRecommendationResults>(undefined);

  const buildShopUrl = ({ category, dimensions }: NbsResponse): string => {
    const searchParams = new URLSearchParams();
    dimensions?.number_of_teeth && searchParams.set('number_of_teeth', String(`val_${dimensions.number_of_teeth}`));
    dimensions?.d1 && searchParams.set('d1_bore_diameter', String(Number(dimensions.d1).toFixed(2)));
    dimensions?.module && searchParams.set('modul', String(`val_${dimensions.module}`));
    return defaultGearData[category].shopLink + (searchParams.size ? `?${searchParams.toString()}` : '');
  };

  const preparePayload = (
    nbsResponse: NbsResponse,
    igeRecommendationResults?: IgeResponseData
  ): IgeRecommendationResults => {
    const { category } = nbsResponse;
    const { shopUrl, articleNumber, articlesCount, unitPrice, widenImageId } = igeRecommendationResults || {};
    return {
      nameKey: defaultGearData[category].nameKey,
      category,
      technicalDrawingWidenId: defaultGearData[category].technicalDrawingWidenId,
      data: {
        articleNumber,
        articlesCount,
        widenImageId: widenImageId ?? defaultGearData[category].widen,
        shopUrl: shopUrl ?? buildShopUrl(nbsResponse),
        unitPrice,
      },
    };
  };

  const getIgeRecommendation = async (session: Session, nbsResponse: NbsResponse): Promise<void> => {
    const { sessionId, server } = session;
    const createRequestPayload = (response: NbsResponse): IgeRecommendationRequestPayload => ({
      dimensions: response.dimensions,
    });

    if (nbsResponse.category === 'gears_spur_straight' && nbsResponse.dimensions) {
      const response = await fetch(`${IGE.apiUrl}/cus/spur-gear?sid=${sessionId}&server=${server}`, {
        method: 'POST',
        body: JSON.stringify(createRequestPayload(nbsResponse)),
        headers: {
          'Content-Type': 'application/json',
          'Kopla-Service-Id': IGE.koplaServiceId,
        },
      });

      if (!response.ok) {
        setIgeRecommendations(preparePayload(nbsResponse));
      } else {
        const recommendation = (await response.json()) as { data: IgeResponseData };
        setIgeRecommendations(preparePayload(nbsResponse, recommendation?.data));
      }
    } else {
      setIgeRecommendations(preparePayload(nbsResponse));
    }
  };

  return { getIgeRecommendation, setIgeRecommendations, igeRecommendation };
};
